import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faBullhorn,
  faGavel,
  faUsers,
  faBookOpen,
  faHeart,
  faShare,
  faCheck,
} from '@fortawesome/free-solid-svg-icons';
import {
  faFacebookF,
  faInstagram,
  faLinkedinIn,
  faXTwitter,
} from '@fortawesome/free-brands-svg-icons';
import './ActionItems.css';

/**
 * ActionItems - Call-to-action section with 5 action items
 *
 * Features:
 * - Numbered action items with icons
 * - Bold title with description for each item
 * - Visually distinctive styling to encourage engagement
 */
export function ActionItems() {
  const { t } = useTranslation();
  const [copied, setCopied] = useState(false);
  const toolUrl = 'https://liftourvoices.org/lov-where-you-work-ai-tool';

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(toolUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      // Fallback for older browsers
      const textArea = document.createElement('textarea');
      textArea.value = toolUrl;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const socialLinks = [
    { icon: faFacebookF, url: 'https://www.facebook.com/LiftOurVoicesUS/', label: 'Facebook' },
    { icon: faInstagram, url: 'https://www.instagram.com/liftourvoices', label: 'Instagram' },
    { icon: faLinkedinIn, url: 'https://www.linkedin.com/company/lift-our-voices/', label: 'LinkedIn' },
    { icon: faXTwitter, url: 'https://x.com/LiftOurVoicesUS', label: 'X (Twitter)' },
  ];

  const actionItems = [
    {
      id: 1,
      icon: faBullhorn,
      titleKey: 'actionItems.items.spread.title',
      descKey: 'actionItems.items.spread.description',
    },
    {
      id: 2,
      icon: faGavel,
      titleKey: 'actionItems.items.arbitration.title',
      descKey: 'actionItems.items.arbitration.description',
    },
    {
      id: 3,
      icon: faUsers,
      titleKey: 'actionItems.items.networks.title',
      descKey: 'actionItems.items.networks.description',
    },
    {
      id: 4,
      icon: faBookOpen,
      titleKey: 'actionItems.items.laws.title',
      descKey: 'actionItems.items.laws.description',
    },
    {
      id: 5,
      icon: faHeart,
      titleKey: 'actionItems.items.follow.title',
      descKey: 'actionItems.items.follow.description',
    },
  ];

  return (
    <section className="action-items-section">
      <div className="action-items-container">
        <h2 className="action-items-title">{t('actionItems.title')}</h2>
        <div className="action-items-list">
          {actionItems.map((item) => (
            <div key={item.id} className="action-item">
              <div className="action-item-number">
                <span>{item.id}</span>
              </div>
              <div className="action-item-icon">
                <FontAwesomeIcon icon={item.icon} />
              </div>
              <div className="action-item-content">
                <span className="action-item-title">{t(item.titleKey)}</span>{' '}
                <span className="action-item-description">{t(item.descKey)}</span>
                {/* Share button under first action item */}
                {item.id === 1 && (
                  <div className="action-item-share-link">
                    <button
                      className={`share-button ${copied ? 'copied' : ''}`}
                      onClick={handleCopyLink}
                      aria-label={copied ? 'Link copied!' : 'Share link'}
                    >
                      <FontAwesomeIcon icon={copied ? faCheck : faShare} />
                      <span>{copied ? 'Link Copied!' : 'Share'}</span>
                    </button>
                  </div>
                )}
                {/* Social media icons under last action item */}
                {item.id === 5 && (
                  <div className="action-item-social-links">
                    {socialLinks.map((social) => (
                      <a
                        key={social.label}
                        href={social.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="social-icon-link"
                        aria-label={social.label}
                      >
                        <FontAwesomeIcon icon={social.icon} />
                      </a>
                    ))}
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>

        {/* Donate button at bottom */}
        <div className="action-items-donate">
          <a
            href="https://www.every.org/lift-our-voices-ec18?donateTo=lift-our-voices-ec18#/donate/card"
            target="_blank"
            rel="noopener noreferrer"
            className="donate-button-action"
          >
            {t('common.donate')}
          </a>
        </div>
      </div>
    </section>
  );
}
